import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { withTranslator } from '../HOCComponents/withTranslator'
import styled from 'styled-components'
import { UIStyles } from '../../theme'
import debounce from 'lodash/debounce'
import Divider from '@mui/material/Divider'
import { StyledTooltip } from '../UIComponents/StyledTooltip'
import { CHUNK_SIZE } from '@layers-frontend/commons/constants'

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 364px;
  height: 60px;
  z-index: 2;
  background-color: transparent;
`

// Translucent pill field with a white rim and a soft white focus halo (the top bar chrome is white).
// border-box keeps the 255x36 footprint with the 1px border.
const SearchBarWrapper = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 255px;
  height: 60%;
  border: 1px solid ${UIStyles.whiteColor};
  background-color: ${UIStyles.whiteTransparent01};
  border-radius: 18px;
  margin-left: 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus-within {
    border-color: ${UIStyles.whiteColor};
    box-shadow: 0 0 0 3px ${UIStyles.lightGrayHoverColor};
  }
`

const InputWrapper = styled.input`
  display: flex;
  width: 90%;
  height: 100%;
  font-family: Roboto;
  font-size: 18px;
  letter-spacing: 1px;
  color: white;
  caret-color: ${UIStyles.whiteColor};
  padding-left: 14px;
  padding-right: 10px;
  border: none;
  outline: none;
  background-color: transparent;
  border-radius: 18px;
  &::placeholder {
    color: ${UIStyles.whiteTransparent06};
  }
  &:focus::placeholder {
    color: transparent;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ButtonWrapper = styled.button`
  height: 100%;
  width: 15%;
  border-radius: 5px;
  border: none;
  background-color: transparent;
  cursor: pointer;
`

const IconWrapper = styled.i`
  color: ${UIStyles.whiteColor};
  font-size: 1.3em;
  :hover {
    color: ${UIStyles.lightGrayColor};
  }
`

const SearchBar = ({ fieldCount, disabled, isFocus, isVisible, toggleFocusSearchBar, setSearchInputTextFilters, resetSearchInputFilters, children, t }) => {
  const [searchInput, setSearchInput] = useState('')
  const [activeSearch, setActiveSearch] = useState(false) // activate search input on key enter or search button (big clients)
  const inputRef = useRef(null)

  const setQuery = debounce(setSearchInputTextFilters, 1000)
  const showSearchButton = fieldCount > CHUNK_SIZE

  const handleFilterDelete = () => {
    if (searchInput !== '') {
      setSearchInput('')
      if (activeSearch) resetSearchInputFilters()
    }
  }

  const handleFilterSearch = () => {
    setActiveSearch(true)
    setQuery(searchInput)
  }

  const handleOnFocus = () => {
    toggleFocusSearchBar(true)
  }

  const openSearchFilters = () => {
    if (!isFocus) openSearchFilters()
  }

  if (!isVisible) return null

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      setActiveSearch(true)
      setQuery(searchInput)
    }
  }

  const handleFilterTextInputChange = e => {
    const { value } = e.target

    if (fieldCount < CHUNK_SIZE) {
      setActiveSearch(true)
      setSearchInput(value)

      setQuery(value)
    } else {
      setActiveSearch(false)
      setSearchInput(value)
    }
  }

  return (
    <SearchWrapper>
      <SearchBarWrapper id="search">
        <InputWrapper
          placeholder={disabled ? t('loading') + '...' : t('search')}
          inputProps={{ 'aria-label': 'search' }}
          ref={inputRef}
          id="input-search-general"
          data-testid="search-general-input"
          placeholderTextColor="white"
          value={searchInput}
          onFocus={handleOnFocus}
          disabled={disabled}
          onChange={handleFilterTextInputChange}
          onClick={openSearchFilters}
          onKeyUp={e => handleKeyPress(e)}
          autoComplete="off"
          type="search"
          name="search"
          spellcheck="false"
        />
        {searchInput.length > 0 && (
          <ButtonWrapper onClick={handleFilterDelete} id="search-delete">
            <StyledTooltip title={t('clear')}>
              <IconWrapper className="ion-android-close" data-testid="remove-selection-button" />
            </StyledTooltip>
          </ButtonWrapper>
        )}
        {showSearchButton && (
          <>
            <Divider orientation="vertical" />
            <ButtonWrapper onClick={handleFilterSearch} id="search-submit" data-testid="search-submit-button">
              <StyledTooltip title={t('Search')}>
                <IconWrapper className="ion-android-search" />
              </StyledTooltip>
            </ButtonWrapper>
          </>
        )}
      </SearchBarWrapper>
      {children}
    </SearchWrapper>
  )
}

SearchBar.propTypes = {
  isFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  isVisible: PropTypes.bool,
  toggleFocusSearchBar: PropTypes.func,
  setSearchInputTextFilters: PropTypes.func,
  resetSearchInputFilters: PropTypes.func,
  openSearchFilters: PropTypes.func,
  fieldCount: PropTypes.number
}

export default withTranslator(SearchBar)
